import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import { PersonOff } from '@mui/icons-material';

export const STATUS_STYLES = {
  pending: { bg: '#fff7e6', color: '#8a5a00' },
  approved: { bg: '#e8f5e9', color: '#1b5e20' },
  rejected: { bg: '#fdecea', color: '#b71c1c' },
  cancelled: { bg: '#eceff3', color: '#455a64' },
  fully_approved: { bg: '#e8f5e9', color: '#1b5e20' },
};

export const REQUEST_TYPE_TABS = [
  { key: 'to_leave', label: 'Leave', Icon: ExitToAppIcon },
  { key: 'loans', label: 'Loans', Icon: MonetizationOnIcon },
  { key: 'finance_claim', label: 'Finance Claim', Icon: AttachMoneyIcon },
  { key: 'misc', label: 'Misc', Icon: MoreHorizIcon },
  { key: 'business_trip', label: 'Business Trip', Icon: BusinessCenterIcon },
  { key: 'bank', label: 'Bank', Icon: AccountBalanceIcon },
  { key: 'resignation', label: 'Resignation', Icon: PersonOff },
  { key: 'personal_data_change', label: 'Personal Data', Icon: ManageAccountsIcon },
  { key: 'missing_punch', label: 'Missing Punch', Icon: PunchClockIcon },
  { key: 'pre_approval', label: 'Pre-Approval', Icon: FactCheckIcon },
];

export const normalizeRoleKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const HR_ROLE_ALIASES = new Set([
  'hr_admin',
  'admin_hr',
  'hr',
  'human_resource',
  'human_resources',
  'hr_administration',
  'hr_administration_team',
  'hr_team',
  'hr_team_creator',
  'hr_review',
  'hr_officer',
  'hr_supervisor',
  'hr_manager',
  'hr_generalist',
  'people_operations',
  'people_ops',
  'recruiter',
  'talent_acquisition',
]);

export const isHrTeamRole = (role) => HR_ROLE_ALIASES.has(normalizeRoleKey(role));

// Only the canonical "HR Admin" role is treated as an HR-side approver of
// requests. Every other HR-team alias (hr, hr_team, hr_manager, hr_officer,
// hr_generalist, people_operations, recruiter, etc.) acts like a regular
// employee for the request workflow — they create their own requests,
// they author Pre-Approval submissions, and they handle revisions, but
// they don't see the cross-tenant Assigned / Archive approval queues.
export const HR_ADMIN_ROLE_ALIASES = new Set([
  'hr_admin',
  'admin_hr',
  // HR Coordinator is an HR-admin-level approver: it gets the Assigned/Archive
  // approval queues just like hr_admin (self-approval is blocked server-side).
  'hr_coordinator',
]);

export const isHrAdminRole = (role) => HR_ADMIN_ROLE_ALIASES.has(normalizeRoleKey(role));

// Roles that can create / submit a Pre-Approval request. Includes HR team
// roles, plus the management roles that participate in the pre-approval
// chain (HOD, Operations Manager, CEO, Executive). Employees, regular
// line managers, finance, and finance coordinators are excluded.
export const PRE_APPROVAL_AUTHOR_ROLE_ALIASES = new Set([
  ...HR_ROLE_ALIASES,
  'hod',
  'head_of_department',
  'head_department',
  'head_of_dept',
  'operation_manager',
  'operations_manager',
  'operations',
  'operation',
  'ceo',
  'executive',
  'executive_manager',
  'executive_director',
  'executive_managing_director',
]);

export const canAuthorPreApprovalRequest = (role) =>
  PRE_APPROVAL_AUTHOR_ROLE_ALIASES.has(normalizeRoleKey(role));

export const getLocalRole = () => {
  const candidates = [
    localStorage.getItem('actual_role'),
    localStorage.getItem('role'),
    localStorage.getItem('user_role'),
    localStorage.getItem('userRole'),
  ]
    .map((value) => normalizeRoleKey(value))
    .filter(Boolean);

  // NOTE: `actual_role` is the "Actual Role / Appraisal Category" (e.g. hr_team)
  // and is listed first, but it is NOT the access role. If an elevated access
  // role (hr_admin / hr_coordinator) is present in any field — usually `role` —
  // prefer it, so request gating (the Assigned tab, etc.) isn't masked by the
  // appraisal category. Non-HR-admin users keep the original first-field order.
  const hrAdmin = candidates.find((role) => isHrAdminRole(role));
  if (hrAdmin) return hrAdmin;

  return candidates[0] || 'employee';
};

export const getLocalEmployeeCode = () =>
  localStorage.getItem('employee_code') ||
  localStorage.getItem('employeeCode') ||
  localStorage.getItem('employee_code_view') ||
  '';

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const lastNDays = (n) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (n - 1));
  return { from: formatDate(from.toISOString()), to: formatDate(to.toISOString()) };
};

export const normalizeDisplayValue = (value) => {
  if (value === null || value === undefined) return '-';
  const text = String(value).trim();
  return text ? text : '-';
};

export const formatRequestTypeLabel = (value) =>
  String(value || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const isStatusColumn = (col) =>
  col?.type === 'status' || /status/i.test(String(col?.label || ''));

export const isDetailsColumn = (col) =>
  String(col?.label || '').toLowerCase() === 'details';
